import React, { useState, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Input from '../../../../components/Form/Input'
import Button from '../../../../components/Form/Button'
import Select from '../../../../components/Form/Select'
import Modal from '../../../../components/Modal'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { enrollInOptirateApi, getTargetTariffsApi } from '../../../../apis/apis'
import { CREDIT_PAYEE_LABELS, CreditPayee, UtilityType } from '../../../../apis/types'
interface OptirateEnrollmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void | Promise<void>
  selectedMeterServiceCuid: string
  utilityShortName: UtilityType
  isReoptimization?: boolean
}

const OptirateEnrollmentModal: React.FC<OptirateEnrollmentModalProps> = ({ isOpen, onClose, onSuccess, selectedMeterServiceCuid, utilityShortName, isReoptimization }) => {
  const dispatch = useAppDispatch()
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
  const [targetTariffOptions, setTargetTariffOptions] = useState<Record<string, string>>({})
  const [defaultNewTariff, setDefaultNewTariff] = useState('')
  const [targetTariffsLoading, setTargetTariffsLoading] = useState(false)

  // Add default date calculation
  const d = new Date()
  const defaultDate = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`

  const requireStr = 'This field is required'

  useEffect(() => {
    if (!isOpen) {
      setTargetTariffOptions({})
      setDefaultNewTariff('')
      setTargetTariffsLoading(false)
      return
    }

    let cancelled = false
    setTargetTariffsLoading(true)
    setTargetTariffOptions({})
    setDefaultNewTariff('')

    const fetchTargetTariffs = async () => {
      try {
        const tariffs = await getTargetTariffsApi(selectedMeterServiceCuid, utilityShortName)
        if (cancelled) return

        const options = tariffs.reduce<Record<string, string>>((acc, tariff) => {
          if (tariff?.cuid != null && tariff.schedule != null) {
            acc[tariff.cuid] = tariff.schedule
          }
          return acc
        }, {})

        setTargetTariffOptions(options)
        setDefaultNewTariff(tariffs[0]?.cuid ?? '')
      } finally {
        if (!cancelled) {
          setTargetTariffsLoading(false)
        }
      }
    }

    void fetchTargetTariffs()

    return () => {
      cancelled = true
    }
  }, [isOpen, selectedMeterServiceCuid, utilityShortName])

  if (!isOpen) return null

  const hasTargetTariffs = Object.keys(targetTariffOptions).length > 0
  const canSubmitEnrollment = !targetTariffsLoading && hasTargetTariffs

  return (
    <>
      {confirmModal}
      <CustomContentModal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-2xl">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{isReoptimization ? 'OptiRate Re-enrollment' : 'OptiRate Enrollment'}</h3>
          </div>
          <div className="px-6 py-4">
            <Formik
              enableReinitialize
              initialValues={{
                dateOfTariffChangeRequest: defaultDate,
                newTariff: defaultNewTariff,
                creditPayee: 'PROJECT_CO_REIMBURSE',
              }}
              validationSchema={Yup.object().shape({
                dateOfTariffChangeRequest: Yup.string()
                  .required(requireStr)
                  .matches(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/, 'M/D/YYYY format required'),
                newTariff: Yup.string().required(requireStr),
                creditPayee: Yup.string().required(requireStr).oneOf(Object.keys(CREDIT_PAYEE_LABELS)),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  // Show confirmation modal first
                  await new Promise((resolve, reject) => {
                    setConfirmModal(<Modal resolve={resolve} reject={reject} />)
                  })
                  setConfirmModal(false)

                  try {
                    setSubmitting(true)

                    console.log('Submitting Optirate enrollment for service agreement:', selectedMeterServiceCuid)
                    console.log('Optirate enrollment form values:', values)

                    const response = await enrollInOptirateApi(selectedMeterServiceCuid, values.dateOfTariffChangeRequest, values.newTariff, values.creditPayee as CreditPayee)

                    if (response.success) {
                      dispatch(
                        pushToast({
                          message: isReoptimization ? 'OptiRate re-enrollment submitted successfully' : 'OptiRate enrollment submitted successfully',
                          type: 'success',
                        })
                      )
                      await onSuccess?.()
                    } else {
                      dispatch(
                        pushToast({
                          message: `Error ${isReoptimization ? 're-enrolling' : 'enrolling'} in OptiRate: ${response.message}`,
                          type: 'error',
                        })
                      )
                    }

                    onClose()
                    setSubmitting(false)
                  } catch (e: any) {
                    dispatch(
                      pushToast({
                        message: `Error ${isReoptimization ? 're-enrolling' : 'enrolling'} in OptiRate: ${(e as Error).message}`,
                        type: 'error',
                      })
                    )
                    setSubmitting(false)
                  }
                } catch (e) {
                  // User canceled in the confirmation modal
                  setConfirmModal(false)
                }
              }}
            >
              {({ errors, touched, isSubmitting, isValid }) => (
                <Form>
                  <div className="grid grid-cols-1 gap-4 dark:text-gray-200">
                    <div>
                      <Field
                        as={Input}
                        name="dateOfTariffChangeRequest"
                        label="Date of Tariff Change Request"
                        required
                        error={errors.dateOfTariffChangeRequest && touched.dateOfTariffChangeRequest ? errors.dateOfTariffChangeRequest : undefined}
                      />
                    </div>

                    <div>
                      {targetTariffsLoading ? (
                        <p className="text-sm text-slate-600 dark:text-slate-300">Loading target tariffs…</p>
                      ) : hasTargetTariffs ? (
                        <Field component={Select} name="newTariff" label="New Tariff" options={targetTariffOptions} required />
                      ) : (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          No target tariffs are available for this service agreement. Refresh the page or contact support if
                          enrollment should be allowed.
                        </p>
                      )}
                    </div>

                    <div>
                      <Field component={Select} name="creditPayee" label="Credit Payee" options={CREDIT_PAYEE_LABELS} required />
                    </div>
                  </div>

                  <div className="flex justify-end items-center mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="space-x-2">
                      <Button type="submit" disabled={isSubmitting || !isValid || !canSubmitEnrollment}>
                        Submit Enrollment
                      </Button>
                      <Button type="button" onClick={onClose} color="slate">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </CustomContentModal>
    </>
  )
}

export default OptirateEnrollmentModal
